import {Badge} from '@/components/ui/badge/Badge'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useInfinitePatients} from '@/features/patients/services/useInfinitePatients'
import {PatientSmall} from '@/features/patients/types'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {formatDate} from '@/dayjs'
import {StyledSelectPatientOptionPersonalData, StyledSelectPatientOptionWrapper} from './style'
import {patientServiceTypeMapToLabel} from '@/utilities/helpers'
import {PatientAgeRange} from '@/types'
import {forwardRef, useRef, useState} from 'react'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'

interface InputSelectPatientProps {
    label?: string
    placeholder: string
    errorMessage?: string
    value?: {id: string; fullName: string}
    onChange: (value: PatientSmall | null) => void
    hideBadge?: boolean
    hideDate?: boolean
    disabled?: boolean
    isLoading?: boolean
    onClear?: () => void
}

export const InputSelectPatient = forwardRef<HTMLSelectElement, InputSelectPatientProps>(
    ({label, placeholder, errorMessage, value, onChange, hideBadge, hideDate, disabled, isLoading, onClear}, ref) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const inputPatientRef = useRef(null)

        const patientsQuery = useInfinitePatients(
            {
                limit: 10
            },
            {enabled: isQueryEnabled}
        )

        return (
            <ComboboxProvider setValue={patientsQuery.onSearch} resetValueOnHide>
                <SelectProvider
                    value={value?.id ?? ''}
                    setValue={value =>
                        onChange(patientsQuery.remappedData.find(patient => patient.id == value) ?? null)
                    }
                    setOpen={isOpen => {
                        patientsQuery.onResetSearch(inputPatientRef)
                        setIsQueryEnabled(isOpen)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        {label && <Label>{label}</Label>}
                        <SelectTrigger
                            ref={ref}
                            placeholder={placeholder}
                            fullWidth
                            value={value?.id}
                            displayValue={() =>
                                value?.fullName ? (
                                    <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                        <span>{value.fullName}</span>
                                        {onClear && (
                                            <XCloseIcon
                                                role="button"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    onClear()
                                                }}
                                            />
                                        )}
                                    </Flexbox>
                                ) : null
                            }
                            isLoading={isLoading}
                            hasError={!!errorMessage}
                            disabled={disabled || isLoading}
                        />

                        {errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}

                        <SelectComboboxPopover>
                            <SelectComboboxInput placeholder={t('commons:search_name')} />
                            <SelectComboboxList>
                                {patientsQuery.isLoading ? (
                                    <>
                                        {Array.from({length: 6}, (_, index) => (
                                            <Skeleton key={index} height={32} />
                                        ))}
                                    </>
                                ) : patientsQuery.isError ? (
                                    <InputHelpText error={t('errors:default')} />
                                ) : patientsQuery.remappedData.length > 0 ? (
                                    <SelectVirtualList
                                        data={patientsQuery.remappedData}
                                        totalCount={patientsQuery.remappedData.length}
                                        overscan={100}
                                        endReached={() => patientsQuery.hasNextPage && patientsQuery.fetchNextPage()}
                                        itemContent={(itemIndex, item) => (
                                            <SelectItem hasItemCheckbox key={itemIndex} value={item.id}>
                                                <StyledSelectPatientOptionWrapper
                                                    align="center"
                                                    justify="space-between"
                                                    fullWidth
                                                    gap={2}
                                                >
                                                    <StyledSelectPatientOptionPersonalData align="start" gap={2}>
                                                        <b>{item.fullName}</b>
                                                        {!hideDate && (
                                                            <Text size="textMd" palette="neutral" shade="500">
                                                                {formatDate(item.birthDate, 'DD/MM/YYYY')}
                                                            </Text>
                                                        )}
                                                    </StyledSelectPatientOptionPersonalData>
                                                    {!hideBadge && (
                                                        <Badge size="sm" shape="rectangle">
                                                            {t(
                                                                patientServiceTypeMapToLabel[
                                                                    item.serviceType as Exclude<
                                                                        PatientAgeRange,
                                                                        'evolutionary_age_adults'
                                                                    >
                                                                ]
                                                            )}
                                                        </Badge>
                                                    )}
                                                </StyledSelectPatientOptionWrapper>
                                            </SelectItem>
                                        )}
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
